import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/categories";

const articleHeroImages: Record<string, string> = {
  "article-rumen": "https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20laptop%20and%20notebook%20on%20wooden%20desk%2C%20soft%20natural%20lighting%2C%20minimalist%20aesthetic%2C%20warm%20cream%20tones%2C%20editorial%20photography%20style%2C%20organic%20composition%20with%20plants&width=1200&height=514&seq=article-rumen-lg&orientation=landscape",
  "article-yinqing": "https://readdy.ai/api/search-image?query=Multiple%20computer%20screens%20displaying%20search%20results%20in%20a%20modern%20office%2C%20warm%20ambient%20lighting%2C%20minimalist%20tech%20workspace%2C%20professional%20atmosphere%2C%20clean%20composition&width=1200&height=514&seq=article-yinqing-lg&orientation=landscape",
  "article-anquan": "https://readdy.ai/api/search-image?query=Digital%20security%20concept%20with%20shield%20and%20lock%20icons%20on%20a%20warm%20creamy%20background%2C%20minimalist%20design%2C%20soft%20gradient%20lighting%2C%20professional%20cybersecurity%20aesthetic&width=1200&height=514&seq=article-anquan-lg&orientation=landscape",
  "article-jiqiao": "https://readdy.ai/api/search-image?query=Person%20typing%20on%20keyboard%20with%20magnifying%20glass%20icon%20floating%2C%20warm%20workspace%20with%20coffee%2C%20minimalist%20desk%20setup%2C%20soft%20natural%20light%2C%20clean%20aesthetic&width=1200&height=514&seq=article-jiqiao-lg&orientation=landscape",
  "article-gongju": "https://readdy.ai/api/search-image?query=Modern%20software%20interface%20design%20on%20monitor%20screen%2C%20clean%20UI%20layout%2C%20warm%20ambient%20office%20lighting%2C%20minimalist%20desk%20with%20plants%2C%20professional%20work%20environment&width=1200&height=514&seq=article-gongju-lg&orientation=landscape",
  "article-dianzishu": "https://readdy.ai/api/search-image?query=Elegant%20bookshelf%20with%20organized%20books%20and%20an%20e-reader%20device%2C%20warm%20reading%20nook%20with%20soft%20lamp%20light%2C%20cozy%20minimalist%20interior%2C%20cream%20and%20wood%20tones&width=1200&height=514&seq=article-dianzishu-lg&orientation=landscape",
  "article-yingshi": "https://readdy.ai/api/search-image?query=Home%20theater%20setup%20with%20large%20TV%20screen%20displaying%20movie%20interface%2C%20cozy%20living%20room%20with%20warm%20ambient%20lighting%2C%20minimalist%20entertainment%20space%2C%20cinematic%20atmosphere&width=1200&height=514&seq=article-yingshi-lg&orientation=landscape",
  "article-jiexi": "https://readdy.ai/api/search-image?query=Abstract%20digital%20network%20nodes%20connected%20by%20lines%20on%20warm%20beige%20background%2C%20minimalist%20tech%20concept%2C%20soft%20gradient%2C%20clean%20modern%20design%20aesthetic&width=1200&height=514&seq=article-jiexi-lg&orientation=landscape",
};

const getCategoryLabel = (slug: string): string => {
  const map: Record<string, string> = {
    yingshi: "影视资源",
    ruanjian: "软件资源",
    wendang: "文档资源",
    yinyue: "音乐资源",
    youxi: "游戏资源",
    qita: "其他资源",
  };
  return map[slug] || slug;
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const article = getArticleBySlug(slug || "");
  const relatedArticles = getRelatedArticles(slug || "");

  const heroImage = useMemo(() => {
    if (!article) return articleHeroImages["article-rumen"];
    return articleHeroImages[article.imageSeq] || articleHeroImages["article-rumen"];
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2">
              文章未找到
            </h2>
            <p className="text-foreground-500 mb-6">
              抱歉，您要查看的文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(article.category);
  const categoryLabel = getCategoryLabel(article.category);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "磁力搜索指南",
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleJsonLd),
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                {
                  label: category?.name || "分类",
                  href: `/category/${article.category}`,
                },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="bg-background-50">
          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-5">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs text-accent-600 bg-accent-100/80 px-3 py-1 rounded-full font-medium hover:bg-accent-200/80 transition-colors duration-200 whitespace-nowrap"
                >
                  {categoryLabel}
                </Link>
                <span className="text-xs text-foreground-400">
                  {article.date}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-6 leading-tight">
                {article.title}
              </h1>

              <p className="text-base text-foreground-500 leading-relaxed mb-10">
                {article.excerpt}
              </p>

              <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
                <img
                  src={heroImage}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="prose-custom">
                {article.content.map((block, index) => {
                  if (block.type === "heading" && block.level && block.text) {
                    const HeadingTag = `h${block.level}` as keyof JSX.IntrinsicElements;
                    return (
                      <HeadingTag
                        key={index}
                        className={`font-heading font-bold text-foreground-900 mt-10 mb-4 ${
                          block.level === 2
                            ? "text-2xl md:text-3xl"
                            : "text-xl md:text-2xl"
                        }`}
                      >
                        {block.text}
                      </HeadingTag>
                    );
                  }
                  if (block.type === "paragraph" && block.text) {
                    return (
                      <p
                        key={index}
                        className="text-foreground-600 leading-relaxed mb-5 text-base"
                      >
                        {block.text}
                      </p>
                    );
                  }
                  if (block.type === "list" && block.items) {
                    return (
                      <ul
                        key={index}
                        className="space-y-2 mb-6 pl-1"
                      >
                        {block.items.map((item, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-3 text-foreground-600 text-base"
                          >
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  if (block.type === "tips" && block.text) {
                    return (
                      <div
                        key={index}
                        className="bg-accent-50 border border-accent-200/60 rounded-xl p-5 mb-6"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-lightbulb-line text-accent-600 text-lg"></i>
                          </div>
                          <p className="text-sm text-accent-800 leading-relaxed">
                            <strong>提示：</strong>
                            {block.text}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/60">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-secondary-700 bg-secondary-100/80 px-3 py-1.5 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-18 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}